import Decimal from 'decimal.js';
import { BillingType, ServiceFormDataType } from "../components/BillClientModal/BillClientModal.types";
import { BillClientDisabledReason } from "../components/BillClientModal/BillClientModal.utils";
import { sortAlphabetically } from "../infra/utils";
import { billableStateModel, DiscountType, serviceBillingModel, serviceCostModel, serviceModel } from "../models";
import { NonBillableReason } from "../models/billable-state";
import { BillingStageType } from "../models/manual-service-billing";
import { applyDiscount } from "../pages/AgreementMinisite/BillingHub/BillingControl/BillingControl.utils";
const isReachedMaxCharges = (service, serviceBillableState, items) => {
  const maxCharges = billableStateModel.maxCharges(serviceBillableState);
  const sameServiceItems = items.filter(item => item.type === BillingType.Service && item.service.id === service.id);
  return sameServiceItems.length === maxCharges;
};
const getServiceDisabledReason = (service, items, controlData) => {
  const isAlreadyInList = items.some(item => item.type === BillingType.Service && item.service.id === service.id);
  const serviceBillableState = controlData.manualServices[service.id] ? controlData.manualServices[service.id].billableState : null;
  if (serviceBillingModel.isOneTimeManual(service.billing) && isAlreadyInList) {
    return {
      reasonTooltip: BillClientDisabledReason.oneTimeManualAlreadyInList,
      isDisabled: true
    };
  }
  if (serviceBillableState && isReachedMaxCharges(service, serviceBillableState, items)) {
    return {
      reasonTooltip: BillClientDisabledReason.billedMaxCharges,
      isDisabled: true
    };
  }
  return getNonBillableDisableReason(service, controlData);
};
const getDisableReasonForServiceInStages = (service, items, stage, controlData) => {
  const stageAlreadyInList = items.some(item => item.type === BillingType.Service && item.service.type === ServiceFormDataType.Stages && item.service.stageNumber === stage.stageNumber);
  if (stageAlreadyInList) {
    return {
      reasonTooltip: BillClientDisabledReason.stageAlreadyInList,
      isDisabled: true
    };
  }
  if (stage.type === BillingStageType.Scheduled) {
    return {
      reasonTooltip: BillClientDisabledReason.stageScheduledToBeBilled,
      isDisabled: true
    };
  }
  if (stage.type === BillingStageType.Billed) {
    return {
      reasonTooltip: BillClientDisabledReason.stageAlreadyBilled,
      isDisabled: true
    };
  }
  return getNonBillableDisableReason(service, controlData);
};
export const getNonBillableDisableReason = (service, controlData) => {
  const serviceBillableState = controlData.manualServices[service.id] ? controlData.manualServices[service.id].billableState : null;
  if (serviceBillableState) {
    if (billableStateModel.nonBillableReason(serviceBillableState) === NonBillableReason.BilledMaxCharges) {
      return {
        reasonTooltip: BillClientDisabledReason.billedMaxCharges,
        isDisabled: true
      };
    }
    if (billableStateModel.nonBillableReason(serviceBillableState) === NonBillableReason.IssuingInvoice) {
      return {
        reasonTooltip: BillClientDisabledReason.issuingInvoice,
        isDisabled: true
      };
    }
  }
  return {
    reasonTooltip: BillClientDisabledReason.none,
    isDisabled: false
  };
};
export function filterServicesByStatusAndType(services) {
  return services.filter(service => serviceBillingModel.isManual(service.billing) && !serviceModel.hasUnbillableStatus(service));
}
export function createServiceOptions(service, controlData, items) {
  var _discount$amount, _discount$type;
  const billingStages = controlData.getManualServiceBillingStages(service.id);
  const {
    discount
  } = service.cost;
  const discountAmount = (_discount$amount = discount === null || discount === void 0 ? void 0 : discount.amount()) !== null && _discount$amount !== void 0 ? _discount$amount : new Decimal(0);
  const discountType = (_discount$type = discount === null || discount === void 0 ? void 0 : discount.type()) !== null && _discount$type !== void 0 ? _discount$type : DiscountType.Value;
  if (!billingStages || billingStages.length === 0) {
    return [{
      id: service.id,
      name: service.name,
      costString: serviceCostModel.formattedCost(service.cost),
      disabledReason: getServiceDisabledReason(service, items, controlData)
    }];
  }
  return billingStages.map(stage => {
    return {
      id: service.id,
      name: service.name,
      costString: applyDiscount(stage.amount, discountAmount, discountType),
      disabledReason: getDisableReasonForServiceInStages(service, items, stage, controlData),
      stageNumber: stage.stageNumber,
      billingStages: billingStages
    };
  });
}
export function getBillClientServiceOptions(_ref) {
  let {
    items,
    services,
    controlData
  } = _ref;
  const filteredServices = sortAlphabetically(filterServicesByStatusAndType(services), 'name', {
    ascending: true,
    ignoreCase: true
  });
  return filteredServices.flatMap(service => createServiceOptions(service, controlData, items));
}
export function getGlobalBillServiceOptions(_ref2) {
  let {
    services,
    controlData
  } = _ref2;
  const filteredServices = sortAlphabetically(filterServicesByStatusAndType(services), 'name', {
    ascending: true,
    ignoreCase: true
  });
  return filteredServices.map(service => {
    return {
      id: service.id,
      name: service.name,
      costString: serviceCostModel.formattedCost(service.cost),
      disabledReason: getNonBillableDisableReason(service, controlData)
    };
  });
}
export function getTimeActivitiesServiceOptions(_ref3) {
  let {
    services,
    controlData,
    defaultServiceId
  } = _ref3;
  const filteredServices = services.filter(service => serviceModel.isSuitableForTimeActivities(service) && controlData.manualServices[service.id] && billableStateModel.isBillable(controlData.manualServices[service.id].billableState));
  if (!filteredServices.length) return [];
  const sortedServices = sortAlphabetically(filteredServices, 'name', {
    ascending: true,
    ignoreCase: true
  });
  if (defaultServiceId) {
    const defaultServiceIndex = sortedServices.findIndex(service => service.id === defaultServiceId);
    if (defaultServiceIndex !== -1) {
      const defaultService = sortedServices.splice(defaultServiceIndex, 1)[0];
      sortedServices.unshift(defaultService);
    }
  }
  return sortedServices.map(service => ({
    id: service.id,
    name: service.name,
    costString: serviceCostModel.formattedCost(service.cost)
  }));
}